import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import { INTERNAL_NETWORK_PATH_NAME } from './internal-network.constants';
import {
  InternalNetworkAssetDetailSchema,
  InternalNetworkAssetListQuerySchema,
  InternalNetworkAssetListResponseSchema,
  InternalNetworkAssetMergeResultSchema,
  InternalNetworkAssetRelationshipsResponseSchema,
  InternalNetworkAssetResolutionResultSchema,
} from './internal-network-assets.schema';

const NetworkParamsSchema = z.object({ networkId: z.string() });
const AssetParamsSchema = NetworkParamsSchema.extend({
  assetId: z.string(),
});
const ResolutionIssueParamsSchema = NetworkParamsSchema.extend({
  issueId: z.string(),
});
const ResolutionReasonBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

export const InternalNetworkAssetsContract = terraContract
  .prefix(`/${INTERNAL_NETWORK_PATH_NAME}`)
  .router({
    listAssets: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/assets',
        summary: 'List canonical internal-network assets',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: NetworkParamsSchema,
          query: InternalNetworkAssetListQuerySchema,
        }),
      )
      .output(InternalNetworkAssetListResponseSchema),
    searchAssets: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/assets/search',
        summary: 'Search canonical internal-network assets',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: NetworkParamsSchema,
          query: InternalNetworkAssetListQuerySchema.extend({
            search: z.string().trim().min(1),
          }),
        }),
      )
      .output(InternalNetworkAssetListResponseSchema),
    getAsset: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/assets/{assetId}',
        summary: 'Get canonical internal-network asset',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: AssetParamsSchema }))
      .output(InternalNetworkAssetDetailSchema),
    getAssetRelationships: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/assets/{assetId}/relationships',
        summary: 'Get canonical internal-network asset relationships',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: AssetParamsSchema,
          query: z.object({
            limit: z.coerce.number().int().min(1).max(100).default(50),
          }),
        }),
      )
      .output(InternalNetworkAssetRelationshipsResponseSchema),
    mergeAssets: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/assets/merge',
        summary: 'Merge canonical internal-network assets',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: NetworkParamsSchema,
          body: z.object({
            sourceAssetId: z.string(),
            targetAssetId: z.string(),
            reason: z.string().trim().min(1).max(500),
          }),
        }),
      )
      .output(InternalNetworkAssetMergeResultSchema),
    reverseAssetMerge: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/assets/merge-events/{mergeEventId}/reverse',
        summary: 'Reverse a canonical internal-network asset merge',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: NetworkParamsSchema.extend({ mergeEventId: z.string() }),
          body: ResolutionReasonBodySchema,
        }),
      )
      .output(InternalNetworkAssetMergeResultSchema),
    dismissResolutionIssue: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/assets/resolution-issues/{issueId}/dismiss',
        summary:
          'Dismiss a provisional internal-network asset resolution issue',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ResolutionIssueParamsSchema,
          body: ResolutionReasonBodySchema,
        }),
      )
      .output(InternalNetworkAssetResolutionResultSchema),
    keepAssetsSeparate: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/assets/resolution-issues/{issueId}/keep-separate',
        summary:
          'Keep provisional internal-network assets separate and close the issue',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ResolutionIssueParamsSchema,
          body: ResolutionReasonBodySchema,
        }),
      )
      .output(InternalNetworkAssetResolutionResultSchema),
  });
